import { Observable } from 'rxjs';
import { Cached, CachedGroupOptions, CachedOptions, Source } from './cached';
import { rxCached } from './rx-cached';

export * from './cached';
export * from './rx-cached';

/**
 * A factory to instantiate `Cached<T>`. It caches the data produced by `dataProducer` function
 * for as long as there are subscriptions, or forever if you set the retention policy to do so.
 *
 * @param options Options for creating `Cached` instance, such as:
 *  - `dataProducer` function.
 *  - `id` identity for the cached data.
 *  - `retention` an optional `RetentionPolicy`. Defaults to `RetentionPolicy.Subscribers`.
 *  - `clearDelay` an optional delay-time in milliseconds for clearing the cache.
 *     Defaults to 10_000ms. Applicable only with `RetentionPolicy.Subscribers`.
 *  - `reloadInterval` an optional interval in milliseconds to perform data reload.
 *
 * @usageNotes
 * ```typescript
 * // This will provide caching for data type `<T>` where the data will be produced on first subscription.
 * // The `id` is mainly for logging and tracking what is cached. In most cases you do want to track what
 * // is to be cached, e.g., user's login-name.
 * // You can also change the retention policy and the delay for clearing the cache when zero subscriptions
 * // are detected (relevant only for `RetentionPolicy.Subscriptions`).
 * cached<T>({
 *   dataProducer: () => http.get<T>('URL to fetch something'),
 *   id: 'something',
 *   retention: RetentionPolicy.Forever
 * })
 * ...
 * // In case you anticipate that you need to use a different `dataProducer` function
 * // due to cache ID change then do the following.
 * cached.changeIdentity('new-identity', newDataProducerFunction)
 * ```
 */
export function cached<T>(options: CachedOptions<T>): Cached<T> {
  // At the moment we only have one cache implementation (in memory, based on rxjs mechanisms)
  return rxCached<T>(options);
}

/**
 * Create a logical group of cached data. dataProducer is intended to map the source data to a
 * single object or retrieve new data that depends upon the source data and the merge it all together
 * to a single object.
 * @param sources List of cached data objects that this group depends upon.
 * @param dataProducer Producer function that maps the emitted peer data to a single object that
 *                     is then cached.
 * @param options Additional options, such as:
 *  - `id` identity for the cached data.
 *  - `retention` an optional `RetentionPolicy`. Defaults to `RetentionPolicy.Subscribers`.
 *  - `clearDelay` an optional delay-time in milliseconds for clearing the cache.
 *     Defaults to 10_000ms. Applicable only with `RetentionPolicy.Subscribers`.
 *  - `reloadInterval` an optional interval in milliseconds to perform data reload.
 */
export function cachedGroup<T extends Source<any>[], R>(
  sources: [...T],
  dataProducer: (value: SourceType<T>) => Observable<R>,
  options: CachedGroupOptions<R>
) {
  return rxCached<R>({
    clearDelay: options.clearDelay,
    dataProducer,
    id: options.id,
    retention: options.retention,
    reloadInterval: options.reloadInterval,
    reloadSourcesOnReloadInterval: options.reloadSourcesOnReloadInterval,
    sources,
    watchSourcesLoadingState: options.watchSourcesLoadingState,
  });
}

type SourceType<T extends any[]> = {
  [K in keyof T]: T[K] extends Source<infer U> ? U : never;
};
